import { useEffect, useState, type ReactNode } from "react";
import { NavLink } from "react-router-dom";
import { useBrand } from "@branding/useBrand";
import { useAppStore, selectCurrentUser } from "@state/store";
import { ROLE_LABELS, initialsOf } from "@data/index";
import { NAV_ITEMS, NAV_GROUPS } from "./nav";

/**
 * White-label chrome (ARCHITECTURE §3.4, §5). Header renders the brand
 * mark/name/tagline; the sidebar is the primary nav; below 860px it becomes a
 * hamburger-triggered drawer so navigation never vanishes. Footer renders the
 * legal text. Everything visual derives from the active brand.
 */
export function AppShell({ children }: { children?: ReactNode }) {
  const brand = useBrand();
  const entities = useAppStore((s) => s.entities);
  const scope = useAppStore((s) => s.scope);
  const setScope = useAppStore((s) => s.setScope);
  const currentUser = useAppStore(selectCurrentUser);
  const signOut = useAppStore((s) => s.signOut);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const initial = (brand.name.trim()[0] || "L").toUpperCase();

  // Close the drawer on Escape.
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setDrawerOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawerOpen]);

  return (
    <div className="app">
      <header className="top">
        <button
          type="button"
          className="nav-toggle"
          aria-label="Open navigation"
          aria-expanded={drawerOpen}
          onClick={() => setDrawerOpen((v) => !v)}
        >
          ☰
        </button>
        <NavLink to="/dashboard" className="brand" aria-label={`${brand.name} home`}>
          <div className="brand-mark" aria-hidden="true">
            {initial}
          </div>
          <div>
            <div className="brand-name">{brand.name}</div>
            <div className="brand-tag">{brand.tagline}</div>
          </div>
        </NavLink>
        <div className="top-spacer" />
        <div className="entity-switch" title="Active entity / consolidated view">
          <label htmlFor="entitySelect" className="visually-hidden">
            Active entity
          </label>
          <select
            id="entitySelect"
            value={scope}
            onChange={(e) => setScope(e.target.value)}
          >
            <option value="consolidated">◆ Consolidated group</option>
            {entities.map((en) => (
              <option key={en.id} value={en.id}>
                {en.name}
              </option>
            ))}
          </select>
        </div>
        {currentUser ? (
          <div className="user-menu">
            <div
              className="avatar"
              title={`Signed in as ${currentUser.name} · ${ROLE_LABELS[currentUser.role]}`}
              aria-hidden="true"
            >
              {initialsOf(currentUser.name)}
            </div>
            <div className="user-meta">
              <div className="user-name">{currentUser.name}</div>
              <div className="user-role">{ROLE_LABELS[currentUser.role]}</div>
            </div>
            <button
              type="button"
              className="btn secondary sm"
              onClick={() => void signOut()}
            >
              Sign out
            </button>
          </div>
        ) : null}
      </header>

      <div className="shell">
        <div
          className={`nav-backdrop${drawerOpen ? " show" : ""}`}
          onClick={() => setDrawerOpen(false)}
          aria-hidden="true"
        />
        <nav className={`side${drawerOpen ? " open" : ""}`} aria-label="Primary">
          {NAV_GROUPS.map((group) => (
            <div key={group}>
              <div className="navlabel">{group}</div>
              {NAV_ITEMS.filter((n) => n.group === group).map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className="navitem"
                  onClick={() => setDrawerOpen(false)}
                >
                  <span className="ico" aria-hidden="true">
                    {item.icon}
                  </span>
                  {item.label}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <main>{children}</main>
      </div>

      <footer className="foot">
        <span>{brand.legalFooter}</span>
        <span className="foot-spacer" />
        <span>v0.1.0 · local-first</span>
        <span aria-hidden="true">·</span>
        <span>UAE VAT 5% · CT 9% · e-invoicing-ready</span>
      </footer>
    </div>
  );
}
